/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useState } from 'react';
import { unstable } from '@broadleaf/admin-components';
import cx from 'classnames';
import { get, head, split } from 'lodash';
import PropTypes from 'prop-types';

const { SimpleModal, SVG } = unstable;

const AuroraHeader = ({ className, components, dispatch, metadata, state }) => (
  <header className={cx(className, 'tw-relative tw-z-10')}>
    <OrderRibbon state={state} />
    <components.TitleBar
      components={components}
      dispatch={dispatch}
      metadata={metadata}
      state={state}
    />
    <components.Nav
      components={components}
      dispatch={dispatch}
      metadata={metadata}
      state={state}
    />
  </header>
);

AuroraHeader.propTypes = {
  className: PropTypes.string
};

const OrderRibbon = ({ state }) => {
  const [openTutorial, setOpenTutorial] = useState(false);
  const { isFetchError, fetchError } = state;
  const color = isFetchError ? 'red-600' : 'yellow-400';

  return (
    <aside
      className={`items-center tw-flex tw-w-full tw-justify-center tw-px-2 tw-bg-${color} tw-text-white md:tw-justify-end md:tw-px-4`}
    >
      {isFetchError ? (
        <div className="tw-flex tw-inline-flex tw-items-center tw-p-2 tw-text-sm tw-font-medium">
          <SVG
            className="tw-mr-2 tw-inline tw-h-4 tw-w-4 tw-fill-current"
            name="exclamation-outline"
          />
          <span>
            {`${head(split(get(fetchError, 'stack'), '\n'))}. ${get(
              fetchError,
              'message'
            )}`}
          </span>
        </div>
      ) : (
        <>
          <OmsWorkflowTutorialModal
            isOpen={openTutorial}
            onClose={() => {
              setOpenTutorial(false);
            }}
          />
          <button
            className="focus:tw-shadow-outline tw-flex tw-inline-flex tw-appearance-none tw-items-center tw-p-2 tw-text-sm tw-font-medium tw-text-gray-900 hover:tw-underline focus:tw-underline focus:tw-outline-none"
            onClick={() => {
              setOpenTutorial(true);
            }}
          >
            <SVG
              className="tw-mr-2 tw-inline tw-h-4 tw-w-4 tw-fill-current"
              name="light-bulb"
            />
            <span>Read tutorial on the Order Management Workflow</span>
          </button>
        </>
      )}
    </aside>
  );
};

const OmsWorkflowTutorialModal = ({ isOpen, onClose }) => {
  return (
    <SimpleModal
      closeOnClickOutside={false}
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title="Order Management Workflow Tutorial"
    >
      <section className="tw-mb-12 tw-mt-4 tw-px-4">
        <SectHeading>Overview</SectHeading>
        <section>
          <SectPara>
            It is important to follow these steps to ensure an order is
            completed and payment is received. The standard flow of an order is
            as follows:
          </SectPara>
          <SectPara>
            <ol
              className="tw-ml-6 tw-mt-4 tw-block tw-pl-4"
              style={{ listStyleType: 'decimal' }}
            >
              <Li>Customer submits an order on the Parts to Go site.</Li>
              <Li>
                A confirmation email is sent to the customer automatically.
              </Li>
              <Li>
                The dealer admin views the Order and marks it as "acknowledged".
              </Li>
              <Li>
                When the customer arrives to pick up the parts or when the parts
                are ready to ship: The dealer admin marks the items as
                "fulfilled".
              </Li>
              <Li>
                If there are any items that are no longer desired or cannot be
                fulfilled: The dealer admin marks them as "cancelled".
              </Li>
              <Li>
                Once all items are marked as fulfilled or cancelled and if
                PayPal or Stripe are enabled: the dealer admin can process
                payment for them.{' '}
                <b className="tw-font-bold tw-text-gray-700">Note</b> that you
                will need to refresh the page after processing payments to see
                the "Return Items" action.
              </Li>
              <Li>
                If any parts need to be returned because they are damaged, the
                wrong part, or no longer wanted: The dealer admin creates a
                "return" for the items.
              </Li>
              <Li>
                Once the returned item has been received: The dealer admin
                "confirms" the return. If payments are enabled, then a refund is
                issued automatically.
              </Li>
            </ol>
          </SectPara>
        </section>
      </section>
      <section className="tw-px-4 tw-pb-8">
        <SectHeading>Definitions</SectHeading>
        <section>
          <ul
            className="tw-ml-6 tw-block tw-pl-4"
            style={{ listStyleType: 'initial' }}
          >
            <Li>
              <LiHeading>Acknowledge:</LiHeading>
              <div>
                <LiPara>
                  This let's Aurora know that the dealer has seen the submitted
                  order and will get into contact with the customer if needed to
                  complete the order.
                </LiPara>
                <LiPara>
                  The "Acknowledge" button can be found by clicking on the
                  dropdown menu button at the top-right of the order screen.
                </LiPara>
              </div>
            </Li>
            <Li>
              <LiHeading>Fulfill:</LiHeading>
              <div>
                <LiPara>
                  This means that the parts are picked up or shipped.
                </LiPara>
                <LiPara>
                  The button to mark items as fulfilled can be found by going to
                  the "Items" tab and viewing items with the "New" status.
                </LiPara>
              </div>
            </Li>
            <Li>
              <LiHeading>Process Payment:</LiHeading>
              <div>
                <LiPara>
                  This means that funds have been transferred to the dealer's
                  account using the payment method provided by the customer.
                </LiPara>
                <LiPara>
                  The button to process payment for items can be found by going
                  to the "Items" tab and viewing items with the "Fulfilled"
                  status.
                </LiPara>
              </div>
            </Li>
            <Li>
              <LiHeading>Confirm Return:</LiHeading>
              <div>
                <LiPara>
                  This means that the dealer has received the returned items and
                  a refund can now be issued, transferring funds back to the
                  customer's account.
                </LiPara>
                <LiPara>
                  The button to mark returned items as confirmed can be found by
                  going to the "Returns" tab and viewing items with the
                  "Pending" status.
                </LiPara>
              </div>
            </Li>
          </ul>
        </section>
      </section>
    </SimpleModal>
  );
};

const SectHeading = ({ children }) => {
  return (
    <header className="tw-mb-6 tw-border-b-2 tw-border-gray-400 tw-pb-1">
      <h2 className="tw-text-2xl tw-font-medium">{children}</h2>
    </header>
  );
};

const SectPara = ({ children }) => {
  return <p className="tw-mb-4 tw-leading-tight last:tw-mb-0">{children}</p>;
};

const Li = ({ children, className }) => {
  return (
    <li
      className={cx('tw-mb-3 tw-leading-tight last:tw-mb-0', {
        [className]: !!className
      })}
    >
      {children}
    </li>
  );
};

const LiHeading = ({ children }) => (
  <b className="tw-float-left tw-mr-1">{children}</b>
);

const LiPara = ({ children }) => (
  <p className="tw-mb-2 last:tw-mb-0">{children}</p>
);

export default AuroraHeader;
export { AuroraHeader };
